import { inject, injectable } from 'inversify'
import * as PIXI from 'pixi.js-legacy'
import AbstractConnect from '../../../framework/src/connect/ajax/AbstractConnect'
import ConnectSocket from '../../../framework/src/connect/socket/ConnectSocket'
import SERVICE_IDENTIFIER from '../../../framework/src/constants/identifiers'
import { wait } from '../../../framework/src/helpers'
import SystemConfig, {
	CONFIG_PARAMS,
} from '../../../framework/src/system/SystemConfig'
import { PLATFORM } from '../../../framework/src/system/SystemPlatform'
import { BASE_ELEMENT_IDS } from '../../../framework/src/ui/BaseDOM'
import IDOM from '../../../framework/src/ui/IDOM'
import { ELEMENT_IDS } from '../../../framework/src/ui/mobile/MobileDOM'
import AbstractUIScreen, {
	STAGE_MODES,
} from '../../../framework/src/ui/screen/AbstractUIScreen'
import { JokerBonusWin } from './JokerBonusWin'
import { JokerWinWrapper } from './JokerWinWrapper'
import { IJackpotNotification, IJokerWinData } from './interfaces/IJackpotWin'
import { IJokerWinBanner } from './interfaces/JokerWinBanner.interface'

import AddResult from '../../../framework/src/connect/result/AddResult'
import { isIphoneChrome } from '../../../framework/src/device/device'
import IAudio from '../../../framework/src/engine/IAudio'
import eventsObservable from '../../../framework/src/events/EventsObservable'
import IGame from '../../../framework/src/game/IGame'
import { GAME_EVENTS } from '../../../framework/src/game/gameEvents'
import LoadingScreen from '../../../framework/src/loader/LoadingScreen'
import StatesManager from '../../../framework/src/states/StatesManager'
import { STATES } from '../../../framework/src/states/states'
import { ACTION_EVENTS } from '../../../framework/src/ui/UserAction'
import DesktopUIScreen from '../../../framework/src/ui/desktop/DesktopUIScreen'
import IScreenData from '../../../framework/src/ui/screen/IScreenData'
import Wallet from '../../../framework/src/wallet/Wallet'
import { BonusInfoTable } from './BonusInfoTable'
import { JokerWinCategory } from './JokerWinCategory'
import { TranslateKeys, TranslateService } from './TranslateService'
import { LevelInfo } from './ajax/LevelInfo'
import { JokerWinThemes } from './ajax/Themes'
import { JokerBonusWinNotifier } from './ajax/WinNotifier'
import {
	assetNames,
	assetPrefix,
	fileNames,
	fileNamesSpine,
} from './constants/assets'
import JOKER_BONUS_IDENTIFIERS from './constants/identifiers'
import { JokerBonusHelpers } from './helpers'
import { IColorTheme } from './interfaces/IColorScheme'
import {
	IJokerBonusWinData,
	IOpenTicket,
	IPagedModel,
} from './interfaces/IJokerBonusWinData'
import { textStyle } from './constants/constants'
import { JokerBonusWinPopup } from './JokerBonusWinPopup'
import axios from 'axios'
import { CurrencyConvert } from './ajax/CurrencyConvert'
import MadmaxMessage from '../../../framework/src/ui/messages/MadmaxMessage'
import { translate } from '../../../framework/src/translations/translations'
import { MESSAGE_EVENTS } from '../../../framework/src/error/errorMessages'
import { ERROR_CODES } from '../../../framework/src/error/errorCodes'

export enum JokerBonusMode {
	asynchronous = 'asynchronous',
	withBet = 'in_spin',
	afterSpin = 'after_spin',
}

/**
 * Main class of Joker bonus feature.
 * Responsible for
 * @export
 * @class JokerWinBanner
 * @implements {IJokerWinBanner}
 */
@injectable()
export class JokerWinBanner implements IJokerWinBanner {
	protected lastData: IJokerWinData[]

	/**
	 * tuple of PIXI.Application objects.
	 * - [0] used for banner, which has enabled event catching
	 * - [1] used for win animation and more bonus info tables, without event catching to not disrupt game controls
	 *
	 * @protected
	 * @type {[PIXI.Application,PIXI.Application]}
	 */
	protected apps: [PIXI.Application, PIXI.Application]
	protected winAnim: JokerBonusWin
	protected wrappers: JokerWinWrapper[] = []
	protected containerPadingTopOrig: number
	protected containerPadingBottomOrig: number
	protected containerPadingPortTopOrig: number
	protected containerPadingPortBottomOrig: number

	protected oldUI: boolean
	protected fullscreenUI: boolean
	protected isMobile: boolean
	stoppedAutostart: boolean = false
	logo: PIXI.Sprite
	logoHead: PIXI.Sprite
	paused: boolean
	protected _jackpots: IJokerBonusWinData[] = []
	protected runningAnim: boolean = false
	currentAnimTime?: number
	levelsLayer: PIXI.Container
	shadow: PIXI.Sprite
	wrapperAnimInProgress: boolean
	winAnimAssetsLoading: boolean = true
	roundRunning: boolean
	runningWrapperPromise: Promise<void>
	groupAnimPromise: Promise<void>
	bannerCreated: boolean = false
	initialized: boolean = false
	displayedInfoTable: boolean
	fetchDataResetPromise: Promise<void>
	fetchDataResetCB: () => void
	silentMode: boolean
	mode: JokerBonusMode
	syncJackpotToRun: IJokerBonusWinData = null
	checkUnclaimedTicketsWhenDone: boolean
	gameReady: boolean = false
	protected fetchPollWait = 8500
	protected offlineText: PIXI.Text

	protected themeCache: Map<number, IColorTheme> = new Map()
	isOffline: boolean = true

	public get jackpots(): IJokerBonusWinData[] {
		return this._jackpots
	}

	constructor(
		@inject(SERVICE_IDENTIFIER.UIScreen)
		protected screen: AbstractUIScreen,
		@inject(JOKER_BONUS_IDENTIFIERS.JOKER_WIN_CONNECT_IDENTIFIER)
		protected connect: AbstractConnect,
		@inject(JOKER_BONUS_IDENTIFIERS.JOKER_BONUS_THEMES)
		protected themes: JokerWinThemes,
		@inject(JOKER_BONUS_IDENTIFIERS.JOKER_BONUS_UNCLAIMED_TICKETS)
		protected openTickets: AbstractConnect,
		@inject(JOKER_BONUS_IDENTIFIERS.JOKER_WIN_LEVEL_INFO_CONNECT_IDENTIFIER)
		protected levelInfoConnect: LevelInfo,
		@inject(JOKER_BONUS_IDENTIFIERS.JOKER_BONUS_CURRENCY_CONVERT)
		protected currencyConvert: CurrencyConvert,
		@inject(SERVICE_IDENTIFIER.SystemConfig)
		protected systemConfig: SystemConfig,
		@inject(SERVICE_IDENTIFIER.DOM) protected dom: IDOM,
		@inject(SERVICE_IDENTIFIER.ConnectWebsocket) protected ws: ConnectSocket,
		@inject(JOKER_BONUS_IDENTIFIERS.JOKER_BONUS_WIN_NOTIFIER)
		protected notifier: JokerBonusWinNotifier,
		@inject(SERVICE_IDENTIFIER.Wallet) protected wallet: Wallet,
		@inject(SERVICE_IDENTIFIER.Game) protected game: IGame,
		@inject(SERVICE_IDENTIFIER.EngineAudio) protected audio: IAudio,
		@inject(SERVICE_IDENTIFIER.Gameset) protected gameset: any,
		@inject(JOKER_BONUS_IDENTIFIERS.JOKER_BONUS_TRANSLATE_SERVICE)
		protected translateService: TranslateService,
		@inject(SERVICE_IDENTIFIER.StatesManager)
		protected statesManager: StatesManager,
		@inject(SERVICE_IDENTIFIER.LoadingScreen)
		protected loadingScreen: LoadingScreen
	) {
		this.oldUI = this.screen.uiType === 'default'
		this.fullscreenUI = this.screen.uiType === 'fullscreen'
		this.isMobile =
			this.systemConfig.get(CONFIG_PARAMS.UI_DEVICE) === PLATFORM.MOBILE
		this.silentMode = true
		this.mode = this.systemConfig.get(CONFIG_PARAMS.JOKER_BONUS_MODE)
		this.setResetPromise()
	}
	private setResetPromise() {
		this.fetchDataResetPromise = new Promise<void>((res) => {
			this.fetchDataResetCB = () => {
				this.setResetPromise()
				res()
			}
		})
	}
	async init() {
		try {
			const useBanner = this.systemConfig.get(CONFIG_PARAMS.JOKER_BONUS)
			const hasUrl =
				!!this.systemConfig.get(CONFIG_PARAMS.URL_JOKER_BONUS_WIN) &&
				!!this.systemConfig.get(CONFIG_PARAMS.URL_JOKER_BONUS_DISPLAY) &&
				!!this.systemConfig.get(CONFIG_PARAMS.URL_JOKER_BONUS_LEVEL_INFO) &&
				!!this.systemConfig.get(CONFIG_PARAMS.URL_JOKER_BONUS_OPEN_TICKETS)
			if (JSON.parse(useBanner) && hasUrl) {
				await this.createBanner()
			}
		} catch (e) {
			throw new Error(`JB init error: ${e.message}`)
		}
	}

	async loadAssets(theme: IColorTheme) {
		PIXI.LoaderResource.prototype._loadElement = function (type) {
			if (this.metadata.loadElement) {
				this.data = this.metadata.loadElement
			} else if (type === 'image' && typeof globalThis.Image !== 'undefined') {
				this.data = new Image()
			} else {
				this.data = document.createElement(type)
			}
			if (this.crossOrigin) {
				this.data.crossOrigin = this.crossOrigin
			}
			if (!this.metadata.skipSource) {
				// Use fetch to track download progress
				fetch(this.url)
					.then((response) => {
						const contentLength = response.headers.get('content-length')
						if (!contentLength) {
							// Handle no content-length
							this.data.src = this.url
							return
						}

						const reader = response.body.getReader()
						const totalBytes = parseInt(contentLength, 10)
						let loadedBytes = 0

						const processChunk = ({ done, value }) => {
							if (done) {
								this.data.src = this.url // Once finished, set image src
								return
							}

							loadedBytes += value.length
							const progress = (loadedBytes / totalBytes) * 100

							// Dispatch custom progress event
							const progressEvent = new Event('progress')
							progressEvent.loaded = loadedBytes
							progressEvent.total = totalBytes
							progressEvent.progress = progress
							progressEvent.lengthComputable = true // Add lengthComputable
							progressEvent.url = this.url
							this.data.dispatchEvent(progressEvent)

							reader.read().then(processChunk)
						}

						reader.read().then(processChunk)
					})
					.catch((error) => {
						console.error('Error loading image:', error)
						this._boundOnError(error)
					})
			}

			this.data.addEventListener('error', this._boundOnError, false)
			this.data.addEventListener('load', this._boundComplete, false)
			this.data.addEventListener('progress', this._boundOnProgress, false)
			if (this.timeout) {
				this._elementTimer = setTimeout(this._boundOnTimeout, this.timeout)
			}
		}

		if (!this.apps) return
		const logoSmallUri = `${JokerBonusHelpers.getConfigUrl()}/${
			theme.setting.themeLogoSmall
		}`
		const logoBigUri = `${JokerBonusHelpers.getConfigUrl()}/${
			theme.setting.themeLogo
		}`

		await new Promise<void>((r) => {
			let needsLoad = false

			for (const key in theme.setting) {
				if (key === 'bonusName') continue
				const setting = theme.setting[key]
				if (typeof setting === 'string') {
					const uri = `${JokerBonusHelpers.getConfigUrl()}/${setting}`
					if (!this.apps[0].loader.resources[uri]) {
						this.apps[0].loader.add(uri)
						needsLoad = true
					}
				} else {
					const uri = `${JokerBonusHelpers.getConfigUrl()}/${setting.iconUrl}`
					if (!this.apps[0].loader.resources[uri]) {
						this.apps[0].loader.add(uri)
						needsLoad = true
					}
				}
			}
			if (needsLoad) {
				this.apps[0].loader.load(() => {
					r()
				})
			} else {
				r()
			}
		})

		const themeLogoSmall = PIXI.Texture.from(logoSmallUri)
		this.logoHead.texture = themeLogoSmall
		const themeLogo = PIXI.Texture.from(logoBigUri)
		this.logo.texture = themeLogo
	}

	async fetchBannerData() {
		try {
			const data: IJokerWinData[] = (await this.connect.fetch()).filter(
				(group) => group.jackpots.length > 0
			)
			this.lastData = data

			if (this.lastData.length === 0) {
				throw 'no jackpot groups'
			}

			if (this.isOffline) {
				await this.createWrappers(this.lastData)
				this.offlineText.visible = false
				this.isOffline = false
				this.onResize()
			}
			const allUpdated = Promise.all(
				this.wrappers.map((wrapper) =>
					wrapper.update(
						data.find((groupData) => wrapper.groupId === groupData.id)
					)
				)
			)
			await Promise.race([
				allUpdated,
				wait(this.fetchPollWait),
				this.fetchDataResetPromise,
			])

			this.fetchBannerData()
		} catch (e) {
			console.log(e)
			const tmp = this.isOffline
			this.isOffline = true
			this.destroyWrappers()
			this.offlineText.visible = true
			if (!tmp) this.onResize()
			// console.warn(`Joker bonus fetch new data failed. Retry in 5s.`, e);
			await wait(15000)
			this.fetchBannerData()
		}
	}

	moveTopBar(moveBy: number) {
		$('.mx-bar-top-desktop').css({
			top: moveBy,
		})
		$('#mxInfoBoxContainer').css({
			top: moveBy,
		})
	}

	resizeButtons(moveBy: number): void {
		// resize and add top offset to all controls and settings menu containers
		$(
			`#${BASE_ELEMENT_IDS.GAME_CONTROLS_WRAPPER}, ` +
				`#${BASE_ELEMENT_IDS.WINTABLE_CONTROLS_WRAPPER}, ` +
				`#${ELEMENT_IDS.MENU_SETTINGS}, ` +
				`#${ELEMENT_IDS.MENU_SETTINGS_BUTTON_CONTAINER}`
		).css({
			height: 'calc(var(--vh)*100)',
			top: moveBy + 'px',
		})
		// resize Burger menu
		$(`#${ELEMENT_IDS.MENU_BURGER}, ` + `#${ELEMENT_IDS.MENU_BURGER_UL}`).css({
			height: 'calc(var(--vh)*100)',
		})
	}

	moveOldUIGameWrapper(moveBy: number): void {
		$(
			// `#${BASE_ELEMENT_IDS.INFO_BOX_CONTAINER}, ` +
			`#${BASE_ELEMENT_IDS.GAME_CONTROLS_WRAPPER}, ` +
				`#${BASE_ELEMENT_IDS.WINTABLE_CONTROLS_WRAPPER}, ` +
				`#${BASE_ELEMENT_IDS.GAME_WRAPPER}`
		).css({
			top: moveBy + 'px',
			'margin-top': '0px',
		})
	}
	moveOldUIInfoBox(moveBy: number): void {
		$(
			`#${BASE_ELEMENT_IDS.INFO_BOX_CONTAINER}`
			// `#${BASE_ELEMENT_IDS.GAME_CONTROLS_WRAPPER}, ` +
			// `#${BASE_ELEMENT_IDS.WINTABLE_CONTROLS_WRAPPER}, ` +
			// `#${BASE_ELEMENT_IDS.GAME_WRAPPER}`
		).css({
			top: moveBy + 'px',
			'margin-top': '0px',
		})
	}

	async createApps(addToLoader: string[]) {
		const appOptions = {
			width: window.innerWidth,
			height: window.innerHeight,
			resolution: devicePixelRatio,
			backgroundAlpha: 0,
			autoResize: true,
		}

		this.apps = [
			new PIXI.Application(appOptions),
			new PIXI.Application(appOptions),
		]

		this.apps[0].view.id = 'joker-bonus-view'
		this.apps[0].renderer.resize(window.innerWidth, window.innerHeight * 0.05)

		this.apps[1].view.id = 'joker-bonus-animation-view'
		this.apps[1].renderer.resize(window.innerWidth, window.innerHeight)
		this.apps[1].stage.scale.set(0.85)

		const createResourceProgressHandler = (appIndex: number, id: string) => {
			const resourceProgressMap = new Map<
				string,
				{ perc: number; chunk: number }
			>()

			this.apps[appIndex].loader.onStart.add(() => {
				this.loadingScreen.updateMultiple(id, 0, 0.05)
			})

			this.apps[appIndex].loader.onComplete.add(() => {
				this.loadingScreen.updateMultiple(id, 100, 0.05)
			})

			this.apps[appIndex].loader.pre((resource, next) => {
				const res = resource as PIXI.LoaderResource

				resourceProgressMap.set(res.name, { perc: 0, chunk: res.progressChunk })

				res.onProgress.add((res, perc) => {
					resourceProgressMap.set(res.name, {
						perc: perc,
						chunk: res.progressChunk,
					})

					let totalProgress = 0
					resourceProgressMap.forEach((value) => {
						totalProgress += (value.perc / 100) * value.chunk
					})

					this.loadingScreen.updateMultiple(id, totalProgress * 100, 0.05)
				})

				next()
			})
		}

		createResourceProgressHandler(0, 'jb0')
		createResourceProgressHandler(1, 'jb1')

		this.apps.forEach((app) => {
			app.view.style.position = 'absolute'
			app.start()
			app.stage.y = 0
			document.getElementById(BASE_ELEMENT_IDS.WRAPPER).appendChild(app.view)
		})
		const loadNow = []

		for (const asset of addToLoader) {
			this.apps[0].loader.add(asset, asset)
		}
		loadNow.forEach((path) => {
			this.apps[0].loader.add(JokerBonusHelpers.getUrl(`[jokerBonus]_${path}`, this.systemConfig))
		})
		for (const fileName of Object.keys(fileNames)) {
			for (const file of fileNames[fileName]) {
				this.apps[1].loader.add(
					`[jokerBonus]_${file}`,
					`${JokerBonusHelpers.getConfigUrl()}/anims/${file}.png`
				)
			}
		}
		for (const fileName of Object.keys(fileNamesSpine)) {
			for (const file of fileNamesSpine[fileName]) {
				this.apps[1].loader.add(
					`[jokerBonus]_${file}`,
					`${JokerBonusHelpers.getConfigUrl()}/anims/${file}.png`
				)
			}
		}
		this.apps[1].loader.load(async () => {
			for (let key of Object.keys(this.apps[1].loader.resources)) {
				const cleanKey = key.replace('[jokerBonus]_', '')
				for (const f in fileNames) {
					if (fileNames[f].includes(cleanKey)) {
						new PIXI.Spritesheet(
							this.apps[1].loader.resources[key].texture.baseTexture,
							await axios
								.get(`${JokerBonusHelpers.getConfigUrl()}/anims/${cleanKey}.json`)
								.then((r) => r.data)
						).parse(() => {})
					}
				}
			}
			this.winAnimAssetsLoading = false
			await this.tryAfterLoad()
		})
		await new Promise<void>((res) => {
			this.apps[0].loader.load(() => {
				res()
			})
		})
	}

	async onGameReady() {
		this.gameReady = true
		await this.tryAfterLoad()
	}

	private tryAfterLoad() {
		if (
			!this.winAnimAssetsLoading &&
			this.bannerCreated &&
			!this.initialized &&
			this.gameReady
		) {
			this.initialized = true
			this.winAnim.init(this.isMobile)
			this.tryRunningJackpotAnims()
		}
	}

	private async tryRunningJackpotAnims() {
		const shoudRunInAsynchronous =
			this.mode === JokerBonusMode.asynchronous && this._jackpots.length > 0
		const shoudRunInSynchronous =
			(this.mode === JokerBonusMode.withBet ||
				this.mode === JokerBonusMode.afterSpin) &&
			this.syncJackpotToRun !== null
		if (shoudRunInAsynchronous || shoudRunInSynchronous) {
			await this.startPlayingJackpotAnims()
		}
	}
	private destroyWrappers() {
		for (const wrapper of this.wrappers) {
			createjs.Tween.removeTweens(wrapper)
			wrapper.destroy({ children: true })
		}
		this.wrappers = []
	}
	private async createWrappers(src: IJokerWinData[]) {
		this.destroyWrappers()
		this.wrappers = await Promise.all(
			src.map(async (el, index) => {
				const cachedTheme = this.themeCache.get(el.id)
				if (cachedTheme === undefined) {
					this.themes.groupId = el.id
					const theme = await this.themes.fetch()
					this.themeCache.set(el.id, theme)
				}

				await this.loadAssets(this.themeCache.get(el.id))
				const res = new JokerWinWrapper(
					el,
					this.screen,
					this.shadow,
					this.shadow.width - this.logo.width - this.logoHead.width,
					this.themeCache.get(el.id),
					this.wrappers.length > 1,
					this.fetchPollWait + 500,
					this.wallet.getDecimalPlaces()
				)
				res.y = index * this.logo.height
				this.levelsLayer.addChildAt(res, index)
				return res
			})
		)
	}
	async createBanner() {
		const legislation = this.systemConfig.get(CONFIG_PARAMS.LEGISLATION)
		const regulation = this.systemConfig.get(CONFIG_PARAMS.REGULATION)

		const shouldDisplayAsJackpot = legislation === 'bel' || regulation === 'bel'

		eventsObservable.subscribe(GAME_EVENTS.GAME_END_ROUND, (_, data) => {
			const shouldStopAsync =
				this.mode === JokerBonusMode.asynchronous && this._jackpots.length > 0
			const shouldStopSync =
				(this.mode === JokerBonusMode.withBet ||
					this.mode === JokerBonusMode.afterSpin) &&
				this.syncJackpotToRun != null
			if (this.game.isAutoRun() && (shouldStopAsync || shouldStopSync)) {
				this.game.setForcespin(false)
				eventsObservable.dispatch(ACTION_EVENTS.ACTION_AUTOSTART_STOP)
				this.stoppedAutostart = true
			}
		})
		eventsObservable.subscribe(GAME_EVENTS.GAME_START_ROUND, (_, data) => {
			this.roundRunning = true
			this.destroyInfoTable()
		})
		eventsObservable.subscribe(
			GAME_EVENTS.GAME_END_ROUND_RECALCED,
			(_, data) => {
				this.roundRunning = false
				this.game.setForcespin(false)
				this.tryRunningJackpotAnims()
			}
		)

		if (
			this.mode === JokerBonusMode.withBet ||
			this.mode === JokerBonusMode.afterSpin
		) {
			eventsObservable.subscribe(
				GAME_EVENTS.GAME_ADD_RESULT,
				(_, { addResult }: { addResult: AddResult }) => {
					const jackpot = addResult.getJackpot()
					if (jackpot) {
						this.syncJackpotToRun = jackpot
					}
				}
			)
		}

		this.paused = true
		this.containerPadingTopOrig = this.screen.ContainerPaddingTop
		if (this.isMobile) {
			this.containerPadingBottomOrig = this.screen.ContainerPaddingBottom
		} else {
			this.containerPadingBottomOrig = this.screen.ContainerPaddingBottom += 50
			this.screen.resize()
		}

		if (this.oldUI && !this.isMobile) {
			this.screen.ContainerPaddingLeft = 0
			this.screen.ContainerPaddingRight = 0
		}
		this.logoHead = new PIXI.Sprite()
		this.logo = new PIXI.Sprite()
		this.logoHead.anchor.set(0, 0.5)
		this.logo.anchor.set(0, 0.5)
		this.logoHead.x = 5

		const screenData = this.screen.getScreenData()
		let newWidth = Math.min(
			window.innerWidth,
			this.oldUI
				? screenData.containerWidthScaled
				: this.screen.BackgroundWidth * screenData.canvasScale
		)
		this.shadow = PIXI.Sprite.from(
			JokerBonusHelpers.linearGradientTexture(newWidth, 50, 90, [
				'#006bcf',
				'#021f4f',
				'#000618',
			])
		)

		this.winAnim = new JokerBonusWin(
			shouldDisplayAsJackpot,
			this.wallet.getDecimalPlaces()
		)

		this.levelsLayer = new PIXI.Container()
		this.levelsLayer.y = this.logo.y
		this.levelsLayer.x = this.logo.x + this.logo.width

		const primaryTextStyle: Partial<PIXI.ITextStyle> = {
			...textStyle,
			letterSpacing: 0,
			fill: 0xffffff,
			fontFamily: 'Audiowide-Regular',
			align: 'right',
			fontSize: 26,
			wordWrap: true,
		}

		try {
			await this.translateService.init()
		} catch (e) {
			let msg = new MadmaxMessage(
				'',
				translate(
					'internal_server_error',
					this.systemConfig.get(CONFIG_PARAMS.LANGUAGE)
				),
				{ Close: { event: MESSAGE_EVENTS.MESSAGE_CLOSE_GAME } },
				this.systemConfig.get(CONFIG_PARAMS.LANGUAGE),
				'type',
				100,
				this.systemConfig,
				ERROR_CODES.JB_TRANSLATION_ERROR
			)
			msg.create()
			msg.openMessage()
			document.body.append(msg.getElement())
			msg.getElement().removeAttribute('style')
			throw e
		}
		this.offlineText = new PIXI.Text(
			this.translateService.translate(TranslateKeys.offline).toUpperCase(),
			primaryTextStyle
		)
		this.offlineText.anchor.set(1, 0.5)
		this.offlineText.position.set(this.shadow.width - 10, this.logo.y)
		window.addEventListener('resize', () => {
			if (isIphoneChrome()) {
				setTimeout(() => {
					this.onResize().then(() => this.onResize)
				}, 400)
			} else {
				this.onResize().then(() => {
					if (this.oldUI && this.isMobile) {
						this.onResize().then(() => this.onResize)
					}
				})
			}
		})
		// this.onResize();
		// this.onResize();
		let firstData: IJokerWinData[]
		try {
			firstData = (await this.connect.fetch()).filter(
				(group) => group.jackpots.length > 0
			)
		} catch (e) {
			firstData = []
		}
		this.lastData = firstData
		const addToLoader = []

		await this.createApps(addToLoader)
		if (firstData.length > 0) {
			await Promise.all(
				firstData.map(async (levelData) => {
					this.themes.groupId = levelData.id
					const theme = await this.themes.fetch()
					this.themeCache.set(levelData.id, theme)
				})
			)
			const theme = this.themeCache.get(firstData[0].id).setting
			const s = document.createElement('style')
			s.innerHTML = `
		.mx-loading-joker-bonus{
			background-image: url('${JokerBonusHelpers.getConfigUrl()}/${
				theme.themeLoaderLogo
			}');
		}
		`
			document.body.appendChild(s)

			this.loadingScreen.addBonusGame(`mx-loading-joker-bonus`)

			await this.createWrappers(firstData)
		}

		this.apps[0].stage.addChild(
			this.shadow,
			this.logoHead,
			this.logo,
			this.offlineText,
			this.levelsLayer
		)
		this.apps[1].stage.addChild(this.winAnim)

		await this.onResize()
		await this.onResize()

		await this.checkUnclaimedTickets()

		this.ws.on('joker_bonus_win', (data) => this.onWin(data))
		this.ws.on('new_jackpot_win', async (_data: IJackpotNotification) => {
			this.currencyConvert.jackpot = _data
			const data = await this.currencyConvert.fetch()
			const alertGroup = this.wrappers.find(
				(el) => el.groupId === data.group_id
			)
			if (!alertGroup) return
			const group = this.wrappers.find((el) => el.groupId === data.group_id)
			if (!group) return
			const category = group.categories
				.find((group) =>
					group.some((el) => el.categoryName === data.level_name)
				)
				.find((el) => el.categoryName === data.level_name)
			if (!category) return
			let popup: JokerBonusWinPopup
			const showPopup =
				this.systemConfig.get(CONFIG_PARAMS.JOKER_BONUS_POPUP) === true
			if (showPopup) {
				popup = new JokerBonusWinPopup(
					category,
					this.screen,
					[this.apps[0].stage.scale.x, this.apps[1].stage.scale.x],
					this.translateService
				)
				popup.y =
					(this.shadow.height * this.apps[0].stage.scale.y + 2) /
					this.apps[1].stage.scale.y
				popup.x = !(this.screen instanceof DesktopUIScreen)
					? this.screen.getScreenData().mode === STAGE_MODES.PORT
						? (-window.innerWidth / 2 + 5) / this.apps[1].stage.scale.x
						: -popup.width / 2
					: (category?.worldTransform?.tx ?? 0) / this.apps[1].stage.scale.x -
					  window.innerWidth / 2 / this.apps[1].stage.scale.x
			}
			const catRotatedCB = () => {
				this.runningAnim = true
				this.destroyInfoTable()
				this.audio.playTrack('JokerBonus_alert', 1)
				if (showPopup) this.apps[1].stage.addChildAt(popup, 0)
			}
			eventsObservable.subscribe('joker_bonus-category-rotated', catRotatedCB)
			await group.onWinNotification(data)
			eventsObservable.unsubscribe('joker_bonus-category-rotated', catRotatedCB)
			if (showPopup) popup.destroy()
			this.runningAnim = false
		})
		this.fetchBannerData()

		eventsObservable.subscribe(
			'joker_bonus-pause',
			(name: string, data: JokerWinCategory) => {
				if (this.runningAnim) {
					return
				}
				this.destroyInfoTable()
				this.displayedInfoTable = true
				this.pause()
				const bonusInfoTable = new BonusInfoTable(
					data,
					this.screen,
					this.levelInfoConnect,
					[this.apps[0].stage.scale.x, this.apps[1].stage.scale.x],
					this.translateService,
					this.wallet.getDecimalPlaces()
				)
				bonusInfoTable.y =
					(this.shadow.height * this.apps[0].stage.scale.y + 2) /
					this.apps[1].stage.scale.y
				bonusInfoTable.x = !(this.screen instanceof DesktopUIScreen)
					? this.screen.getScreenData().mode === STAGE_MODES.PORT
						? (-window.innerWidth / 2 + 5) / this.apps[1].stage.scale.x
						: -bonusInfoTable.width / 2
					: (data?.worldTransform?.tx ?? 0) / this.apps[1].stage.scale.x -
					  window.innerWidth / 2 / this.apps[1].stage.scale.x
				this.apps[1].stage.addChild(bonusInfoTable)
			}
		)
		eventsObservable.subscribe('joker_bonus-resume', () => {
			this.onResumeEvent()
		})
		this.paused = false
		this.bannerCreated = true
		await this.tryAfterLoad()
	}
	private async checkUnclaimedTickets(
		tryRunningAfter: boolean = false
	): Promise<boolean> {
		this.checkUnclaimedTicketsWhenDone = false
		const openTicketsData: IPagedModel<IOpenTicket> =
			await this.openTickets.fetch()
		if (openTicketsData.data.length > 0) {
			this.checkUnclaimedTicketsWhenDone =
				openTicketsData._meta_data.pagination.total_records >
				openTicketsData.data.length
			const jackpots: Partial<IJokerBonusWinData>[] = openTicketsData.data.map(
				({
					player_casino_id,
					created,
					currency,
					credit_value,
					display_value,
					id,
					jackpot_id,
					player_mongo_id,
					level_name,
				}) => {
					return {
						casino_id: player_casino_id,
						created,
						currency,
						jackpot_id,
						player_id: player_mongo_id,
						display_value,
						credit_value,
						ticket_id: id,
						level_name,
					}
				}
			)
			this.jackpots.push(...(jackpots as IJokerBonusWinData[]))
			if (tryRunningAfter) {
				this.tryRunningJackpotAnims()
			}
			return true
		}
		return false
	}

	pause() {
		this.paused = true
		this.wrappers[0].pause()
	}
	async onResumeEvent() {
		this.destroyInfoTable()
		this.paused = false
		await wait(1000)
		if (!this.paused) {
			this.resume()
		}
	}

	resume() {
		this.paused = false
		if (this.wrapperAnimInProgress) {
			this.wrappers[0].resume()
		} else {
			this.groupAnimPromise = this.runGroupAnims()
		}
	}

	async runGroupAnims(): Promise<void> {
		if (this.paused) {
			return
		}
		if (this.isOffline) return
		this.wrapperAnimInProgress = true
		this.runningWrapperPromise = this.wrappers[0].run()
		await this.runningWrapperPromise
		if (this.wrappers.length > 1) {
			await Promise.all(
				this.wrappers.map(
					(wrapper, index) =>
						new Promise<void>((res) => {
							wrapper.interactive = false
							wrapper.interactiveChildren = false
							const currentAnimTime = this.currentAnimTime || 0
							const wrapperTween = createjs.Tween.get(wrapper, {
								stopped: true,
								loop: false,
								override: true,
							})
							wrapperTween.to({ y: this.shadow.height * (index - 1) }, 1000)
							if (index === 0) {
								wrapperTween
									.to({ y: this.shadow.height * (this.wrappers.length - 1) }, 0)
									.call(() => {
										this.wrappers.push(this.wrappers.shift())
										this.levelsLayer.setChildIndex(
											wrapper,
											this.levelsLayer.children.length - 1
										)
									})
							}
							wrapperTween.call(() => {
								wrapper.interactive = true
								wrapper.interactiveChildren = true
								res()
							})
							wrapperTween.gotoAndPlay(currentAnimTime)
						})
				)
			)
		}
		this.wrapperAnimInProgress = false
		this.groupAnimPromise = this.runGroupAnims()
	}

	onWin(data: IJokerBonusWinData) {
		if (
			this._jackpots.findIndex(
				(jackpot) =>
					jackpot.jackpot_id === data.jackpot_id &&
					jackpot.ticket_id === data.ticket_id
			) === -1
		) {
			this._jackpots.push(data)
		}
		if (this.winAnimAssetsLoading || this.roundRunning) {
			return
		}
		if (
			this.mode === JokerBonusMode.withBet ||
			this.mode === JokerBonusMode.afterSpin
		) {
			return
		}
		this.startPlayingJackpotAnims()
	}
	protected async startPlayingJackpotAnims() {
		this.pause()
		this.wrappers.forEach((wrapper) => {
			wrapper.interactive = false
			wrapper.interactiveChildren = false
		})
		this.destroyInfoTable()
		await this.statesManager.setState(STATES.JOKER_BONUS_WIN)
		this.statesManager.lock()
		const playingTracks: string[] = []
		this.audio.getTrackNames().forEach((track) => {
			if (this.audio.isPlaying(track) && track !== 'Jackpot_sound') {
				playingTracks.push(track)
				this.audio.fadeOutTrack(track, 1000, false)
			}
		})
		this.audio.lockVolumes()
		if (this.mode === JokerBonusMode.asynchronous) {
			await this.runJackpotsAnimAsync()
		} else {
			await this.runJackpotAnimSync()
		}
		this.statesManager.unlock()
		await this.statesManager.setState(STATES.IDLE)
		this.game.setForcespin(false)
		if (this.checkUnclaimedTicketsWhenDone && this._jackpots.length === 0) {
			this.checkUnclaimedTickets(true)
		}
		this.audio.unlockVolumes()
		for (let track of playingTracks) {
			this.audio.fadeInTrack(track, 1000)
		}

		await this.onJackpotAnimsDone()
	}

	private destroyInfoTable() {
		for (const child of this.apps[1].stage.children) {
			if (child instanceof BonusInfoTable) {
				this.apps[1].stage.removeChild(child)
			}
		}
		this.displayedInfoTable = false
	}

	async runJackpotAnimSync(): Promise<void> {
		const jackpotClaim = this.syncJackpotToRun
		this.syncJackpotToRun = null
		const jackpotId = jackpotClaim.jackpot_id
		const jackpot = this._jackpots.splice(
			this._jackpots.findIndex((jackpot) => jackpot.jackpot_id === jackpotId),
			1
		)[0]
		const combinedJackpotData = { ...jackpot, ...jackpotClaim }
		try {
			let colorTheme = this.themeCache.get(combinedJackpotData.group_id)
			if (colorTheme === undefined) {
				this.themes.groupId = combinedJackpotData.group_id
				colorTheme = await this.themes.fetch()
				this.themeCache.set(combinedJackpotData.group_id, colorTheme)
			}

			this.runningAnim = true
			this.audio.playTrack('Jackpot_sound', 1)
			await this.winAnim.run(combinedJackpotData, colorTheme)
			this.wallet.setBalance(
				combinedJackpotData.credit_value + this.wallet.getBalance()
			)
		} catch (e) {
			console.warn('JB anim failed', e)
		}
		// await this.onJackpotAnimsDone();
	}

	async runJackpotsAnimAsync(): Promise<void> {
		if (this.runningAnim) {
			return
		}

		this.runningAnim = true

		if (this._jackpots.length < 1) {
			// await this.onJackpotAnimsDone();
			return
		}
		const jackpot = this._jackpots.shift()
		this.notifier.jackpotId = jackpot.jackpot_id
		this.notifier.ticketId = jackpot.ticket_id
		try {
			this.game.round.incId()
			this.notifier.roundId = this.game.round.getId()
			this.game.round.incId()
			const jackpotClaim = await this.notifier.fetch()

			const combinedJackpotData = { ...jackpot, ...jackpotClaim }

			let colorTheme = this.themeCache.get(combinedJackpotData.group_id)
			if (colorTheme === undefined) {
				this.themes.groupId = combinedJackpotData.group_id
				colorTheme = await this.themes.fetch()
				this.themeCache.set(combinedJackpotData.group_id, colorTheme)
			}
			this.audio.playTrack('Jackpot_sound', 100)
			await this.winAnim.run(combinedJackpotData, colorTheme)
			this.wallet.setBalance(
				jackpotClaim.credit_value + this.wallet.getBalance()
			)
		} catch (e) {
			console.warn('JB anim failed', e)
			this.game.round.incId()
		}
		this.runningAnim = false
		return this.runJackpotsAnimAsync()
	}

	private async onJackpotAnimsDone() {
		this.runningAnim = false
		this.wrappers.forEach((wrapper) => {
			wrapper.interactive = true
			wrapper.interactiveChildren = true
		})
		if (this.stoppedAutostart) {
			eventsObservable.dispatch(ACTION_EVENTS.ACTION_AUTOSTART_CONTINUE)
		}
		this.stoppedAutostart = false
		this.paused = false
		await wait(1000)
		if (!this.paused) {
			this.resume()
		}
	}

	protected async onResize() {
		if (!this.apps) return
		let screenData = this.screen.getScreenData()
		let w = window.innerWidth
		let h = window.innerHeight
		const hMulti =
			this.isMobile && screenData.mode === STAGE_MODES.LAND ? 0.1 : 0.06

		this.apps[0].renderer.resize(w, h * hMulti)
		this.apps[1].renderer.resize(w, h)

		this.apps[0].view.style.width = `${w}px`
		this.apps[0].view.style.height = `${h * hMulti}px`
		this.apps[1].view.style.width = `${w}px`
		this.apps[1].view.style.height = `${h}px`
		let newWidth = Math.min(
			w,
			this.oldUI
				? screenData.containerWidthScaled
				: this.screen.BackgroundWidth * screenData.canvasScale
		)
		this.apps[0].stage.x = Math.max(0, (w - newWidth) / 2)

		this.apps[1].stage.scale.set(screenData.canvasScale + 0.15)

		//kill current shadow, its texture can be fucked up
		this.shadow.destroy()
		//create new shadow with guaranteed correct texture
		this.shadow = PIXI.Sprite.from(
			JokerBonusHelpers.linearGradientTexture(newWidth, h * hMulti, 90, [
				'#000618',
				'#021f4f',
				'#000618',
			])
		)
		this.apps[0].stage.addChildAt(this.shadow, 0)
		if (this.isOffline) {
			this.logo.height = 0
			this.logoHead.height = 0
		} else {
			if (this.isMobile || newWidth < 1100) {
				this.logoHead.height = this.shadow.height
				this.logo.height = 0
			} else {
				this.logoHead.height = newWidth < 1100 ? this.shadow.height : 0
				this.logo.height = newWidth < 1100 ? 0 : this.shadow.height * 0.6
			}
		}
		this.logoHead.scale.x = this.logoHead.scale.y
		this.logoHead.y = this.shadow.height / 2
		this.logo.scale.x = this.logo.scale.y
		this.logo.position.set(
			this.logoHead.x + this.logoHead.width,
			this.shadow.height / 2
		)

		this.offlineText.style.wordWrapWidth =
			(this.shadow.width - 10) / screenData.canvasScale
		this.offlineText.position.set(
			this.shadow.width - 10,
			this.shadow.height / 2
		)
		this.offlineText.scale.set(screenData.canvasScale)

		this.levelsLayer.y = this.logo.y
		this.levelsLayer.x = this.logo.x + this.logo.width

		this.runningWrapperPromise = null
		this.groupAnimPromise = null
		if (!this.isOffline) {
			await this.createWrappers(this.lastData ?? [])
		}
		this.apps[1].stage.position.set(window.innerWidth / 2, 0)

		this.recalculatePadding(screenData)

		this.screen.resize()
		if (this.oldUI && !this.isMobile) {
			const top = $('#container').css('top')
			$('#joker-bonus-view').css('top', top)
			const height = parseFloat($('#container').css('height')) * (1 + hMulti)
			this.apps[1].renderer.resize(w, height)
			$('#joker-bonus-animation-view').css('top', top).css('height', height)
		}
		// 	if (this.layers instanceof FullscreenPixiLayers) {
		// 		this.layers.getMenu().y = 0;
		// 	}
		// 	// $(`.mx-controls-wrapper`).css({
		// 	// 	top: this.wrappers[0].getMaskedHeight() * this.wrappers[0].scale.y,
		// 	// });
		// }
		this.winAnim.onResize()
		this.groupAnimPromise = this.runGroupAnims()
		this.fetchDataResetCB()
	}

	private recalculatePadding(screenData: IScreenData) {
		const baseBannerHeight = this.shadow.height * this.apps[0].stage.scale.y
		const divider = this.oldUI ? screenData.canvasScale : screenData.canvasScale
		const offset = baseBannerHeight / divider
		this.screen.ContainerPaddingTop = this.containerPadingTopOrig + offset
		if (!(this.oldUI && this.isMobile))
			this.screen.ContainerPaddingBottom =
				this.containerPadingBottomOrig - offset

		if (!this.oldUI) {
			if (this.isMobile) {
				this.resizeButtons(this.apps[0].stage.height)
			} else {
				this.moveTopBar(baseBannerHeight)
			}
		} else {
			if (this.isMobile) {
				this.moveOldUIGameWrapper(baseBannerHeight)
				// this.moveOldUIInfoBox(offset);
			}
		}
	}
}
